import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { ArrowLeft, Download, Printer, X } from "lucide-react";
import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";

interface ReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  receiptId?: string;
  children: React.ReactNode;
  onPrintComplete?: () => void;
  onDownloadComplete?: () => void;
  showPrintButton?: boolean;
  navigateToDashboard?: string;
  showDownloadButton?: boolean;
  showCloseButton?: boolean;
  maxWidth?: string;
  footerActions?: React.ReactNode;
  className?: string;
  autoDownload?: boolean;
  autoPrint?: boolean;
}

export const ReceiptModal: React.FC<ReceiptModalProps> = ({
  isOpen,
  onClose,
  title = "Receipt",
  receiptId,
  children,
  onPrintComplete,
  onDownloadComplete,
  showPrintButton = true,
  showDownloadButton = true,
  showCloseButton = true,
  maxWidth = "max-w-xl",
  footerActions,
  className = "",
  navigateToDashboard,
  autoDownload = false,
  autoPrint = false,
}) => {
  const receiptRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const handlePrint = () => {
    const receiptContent = receiptRef.current;
    if (!receiptContent) return;

    // Create a new window for printing
    const printWindow = window.open("", "_blank", "width=800,height=600");

    if (printWindow) {
      // Get computed styles from current document
      const styles = Array.from(document.styleSheets)
        .map((styleSheet) => {
          try {
            return Array.from(styleSheet.cssRules)
              .map((rule) => rule.cssText)
              .join("\n");
          } catch (e) {
            // Handle CORS issues with external stylesheets
            return "";
          }
        })
        .join("\n");

      // Write HTML with inline styles
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Receipt ${receiptId || ""}</title>
            <style>
              /* Reset styles */
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                padding: 20px;
                background: white;
                color: #000;
              }
              
              /* Print styles */
              @media print {
                body {
                  padding: 0;
                }
                
                @page {
                  margin: 1cm;
                  size: A4;
                }
              }
              
              /* Copy existing styles */
              ${styles}
              
              /* Additional print-specific styles */
              .text-3xl { font-size: 1.875rem; line-height: 2.25rem; }
              .text-xl { font-size: 1.25rem; line-height: 1.75rem; }
              .text-lg { font-size: 1.125rem; line-height: 1.75rem; }
              .text-sm { font-size: 0.875rem; line-height: 1.25rem; }
              .text-xs { font-size: 0.75rem; line-height: 1rem; }
              .font-bold { font-weight: 700; }
              .font-semibold { font-weight: 600; }
              .font-medium { font-weight: 500; }
              .text-gray-900 { color: #111827; }
              .text-gray-600 { color: #4b5563; }
              .text-gray-500 { color: #6b7280; }
              .text-blue-600 { color: #2563eb; }
              .text-orange-900 { color: #7c2d12; }
              .text-orange-800 { color: #9a3412; }
              .text-orange-600 { color: #ea580c; }
              .bg-gray-50 { background-color: #f9fafb; }
              .bg-orange-50 { background-color: #fff7ed; }
              .bg-orange-100 { background-color: #ffedd5; }
              .border { border-width: 1px; }
              .border-t-4 { border-top-width: 4px; }
              .border-green-500 { border-color: #22c55e; }
              .border-orange-200 { border-color: #fed7aa; }
              .border-t { border-top-width: 1px; }
              .border-gray-200 { border-color: #e5e7eb; }
              .rounded-lg { border-radius: 0.5rem; }
              .rounded-full { border-radius: 9999px; }
              .p-4 { padding: 1rem; }
              .p-5 { padding: 1.25rem; }
              .py-6 { padding-top: 1.5rem; padding-bottom: 1.5rem; }
              .px-4 { padding-left: 1rem; padding-right: 1rem; }
              .pt-3 { padding-top: 0.75rem; }
              .pb-4 { padding-bottom: 1rem; }
              .mb-1 { margin-bottom: 0.25rem; }
              .mb-4 { margin-bottom: 1rem; }
              .mt-1 { margin-top: 0.25rem; }
              .space-y-3 > * + * { margin-top: 0.75rem; }
              .space-y-4 > * + * { margin-top: 1rem; }
              .space-y-6 > * + * { margin-top: 1.5rem; }
              .gap-3 { gap: 0.75rem; }
              .gap-x-8 { column-gap: 2rem; }
              .gap-y-6 { row-gap: 1.5rem; }
              .flex { display: flex; }
              .items-center { align-items: center; }
              .items-start { align-items: flex-start; }
              .justify-between { justify-content: space-between; }
              .text-center { text-align: center; }
              .uppercase { text-transform: uppercase; }
              .grid { display: grid; }
              .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
              .w-5 { width: 1.25rem; }
              .h-5 { height: 1.25rem; }
              .flex-1 { flex: 1 1 0%; }
              .flex-shrink-0 { flex-shrink: 0; }
            </style>
          </head>
          <body>
            ${receiptContent.innerHTML}
            <script>
              // Auto-print when page loads
              window.onload = function() {
                window.print();
                // Close window after printing (optional)
                // window.onafterprint = function() {
                //   window.close();
                // };
              };
            </script>
          </body>
        </html>
      `);

      printWindow.document.close();

      onPrintComplete?.();
    }
  };

  // Handle PDF download functionality
  const handleDownload = async () => {
    if (!receiptRef.current) return;

    try {
      // Dynamically import html2pdf to reduce bundle size
      const html2pdf = (await import("html2pdf.js")).default;

      const options = {
        margin: 10,
        filename: `receipt-${receiptId || Date.now()}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };

      await html2pdf().set(options).from(receiptRef.current).save();
      onDownloadComplete?.();
    } catch (error) {
      console.error("Failed to generate PDF:", error);
    }
  };

  React.useEffect(() => {
    if ((autoDownload || autoPrint) && isOpen) {
      const timer = setTimeout(() => {
        if (autoDownload) {
          handleDownload().then(() => {
            if (onClose) onClose();
          });
        } else if (autoPrint) {
          handlePrint();
          if (onClose) onClose();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [autoDownload, autoPrint, isOpen]);

  if (autoDownload || autoPrint) {
    if (!isOpen) return null;
    return (
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px", width: "800px" }}>
        <div ref={receiptRef} id="receipt-content" className="py-4 bg-white p-8">
          {children}
        </div>
      </div>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          className={`${maxWidth} max-h-[90vh] overflow-y-auto ${className}`}
        >
          {/* Header */}
          <DialogHeader className="border-b pb-4">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-xl font-semibold">
                  {title}
                </DialogTitle>
                {receiptId && (
                  <p className="text-sm text-gray-600 mt-2">
                    Receipt #{receiptId}
                  </p>
                )}
              </div>
            </div>
          </DialogHeader>

          {/* Receipt Content */}
          <div ref={receiptRef} id="receipt-content" className="py-4">
            {children}
          </div>

          {/* Footer Actions */}
          <div className="border-t pt-4 receipt-actions">
            <div className="flex items-center justify-between">
              {/* Left side - Print and Download buttons */}
              <div className="flex items-center gap-2">
                {showPrintButton && (
                  <Button
                    variant="outline"
                    onClick={handlePrint}
                    className="flex items-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Print Receipt
                  </Button>
                )}
                {showDownloadButton && (
                  <Button
                    variant="outline"
                    onClick={handleDownload}
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download Receipt
                  </Button>
                )}
                {navigateToDashboard && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigate(navigateToDashboard);
                      onClose();
                    }}
                    className="flex items-center gap-2 mr-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Dashboard
                  </Button>
                )}
              </div>

              {/* Right side - Custom footer actions or close button */}
              {footerActions || (
                <Button onClick={onClose} variant="default">
                  Close
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
